/**
 * One compact line under the header: how many items ran (or were imported),
 * how many checks were scored, agent and
 * evaluator cost, latency. Where a number
 * came from ("source: timed") is a hover hint, not more text.
 */

import type { ReactNode } from "react";
import { formatCount, formatLatency, formatMoney } from "@/lib/format";
import { useScoringTally } from "../hooks";
import { isRetrying, overallTally, tallyParts } from "../scoring/scoring-tally";
import { completeness, isLive, sourceLabel } from "../status";
import type { ExperimentSummary } from "../types";

function Stat({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 border-line-soft sm:not-first:border-l sm:not-first:pl-5">
      <dt className="text-xs text-muted">{label}</dt>
      <dd
        className={`m-0 font-mono text-[13px] ${hint ? "cursor-help underline decoration-line decoration-dotted underline-offset-4" : ""}`}
        title={hint}
      >
        {children}
      </dd>
    </div>
  );
}

export function ExperimentStats({ experiment: e }: { experiment: ExperimentSummary }) {
  const latency = e.aggregate?.latency_ms;
  const live = isLive(e.status);
  const imported = e.runner === "dataset_output";
  const tally = useScoringTally(e);
  // An older server sends no progress at all: then count the first scoring run.
  const checks = e.scoring?.progress ? overallTally(e) : tally;
  return (
    <dl aria-label="Run summary" className="box flex flex-wrap items-baseline gap-x-5 gap-y-2 px-4 py-2.5">
      <Stat label="Items">
        {imported ? (
          <span>{formatCount(e.counts.done)} imported</span>
        ) : (
          <>
            <span>{completeness(e.counts)}</span>
            {live && <span className="text-muted"> so far</span>}
          </>
        )}
      </Stat>
      {/* While live, the progress block above carries this breakdown. */}
      {imported && checks && !live && (
        <Stat label="Checks" hint="A check is one item scored by one evaluator">
          {tallyParts(checks, isRetrying(e)).join(" · ")}
        </Stat>
      )}
      <Stat label="Agent cost" hint={`Source: ${sourceLabel(e.spend.agent_cost_source)}`}>
        {onlyUnknown(e.spend.agent_cost_source) ? "Unknown" : formatMoney(e.spend.agent_usd)}
      </Stat>
      <Stat label="Evaluator cost" hint="Judge calls made by the workbench">
        {formatMoney(e.spend.evaluator_usd)}
        <span className="text-muted"> of {formatMoney(e.spend.limit_usd)} limit</span>
      </Stat>
      <Stat label="Latency" hint={`Source: ${sourceLabel(latency?.source)}`}>
        {latency ? `p50 ${formatLatency(latency.p50)} · p95 ${formatLatency(latency.p95)}` : "—"}
      </Stat>
    </dl>
  );
}

/** Every item's agent cost is unknown (Orchestrator returns none), so $0.00 would be a lie. */
function onlyUnknown(sources: Record<string, number>): boolean {
  const known = Object.entries(sources).filter(([k, n]) => k !== "unknown" && n > 0);
  return known.length === 0 && (sources.unknown ?? 0) > 0;
}
